import _ from "lodash";
import { useQuery } from "@tanstack/react-query";
import { FilterDropdownProps } from "antd/es/table/interface";
import DropdownSearchFilter from "@lib/components/MiniComponents/TableFilters/DropdownSearchFilter";
import { useMemo } from "react";
import { StyledFlex } from "../Table/index.styled";
import { green, red } from "@ant-design/colors";
import { AppTag } from "@lib/components/AppComponents";
import { apiGetProviders } from "@lib/api/provider";
import { ProviderName } from "../Others";

type ProviderFiltersProps = FilterDropdownProps & {
  dataIndex: string;
  apiData?: any[];
};

const ProviderFilters: React.FC<ProviderFiltersProps> = ({
  confirm,
  setSelectedKeys,
  selectedKeys,
  dataIndex,
  apiData = [],
}) => {
  const {
    data,
    isLoading: isLoadingData,
    isFetching,
  } = useQuery({
    queryKey: ["providers-dropdown"],
    queryFn: () =>
      apiGetProviders({
        sort: "name,ASC",
        limit: 1000,
      }),
  });
  const providersData = data?.data?.data;
  const isLoading = isLoadingData || isFetching;

  // Group apiData by dataIndex and count occurrences
  const groupedCounts = useMemo(() => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return {};
    }

    const counts: Record<string, number> = {};
    // Extract parent path from dataIndex (e.g., "provider.name" -> "provider")
    const dataIndexParts = dataIndex.split(".");
    const parentPath =
      dataIndexParts.length > 1
        ? dataIndexParts.slice(0, -1).join(".")
        : dataIndex;

    apiData.forEach((item) => {
      const parentValue = _.get(item, parentPath);
      if (parentValue !== undefined && parentValue !== null) {
        // For nested objects like customer, use the id as key
        const key =
          typeof parentValue === "object" && parentValue?.id
            ? parentValue.id
            : String(parentValue);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [apiData, dataIndex]);

  const filterData = useMemo(() => {
    if (!providersData) return [];
    return providersData
      .map((provider) => {
        const count = groupedCounts[provider.id] || 0;
        return {
          text: (
            // space between customer name and tag
            <StyledFlex
              style={{ gap: 4, justifyContent: "space-between", width: "100%" }}
            >
              <ProviderName provider={provider} />
              <AppTag
                color={count > 0 ? green[7] : red[7]}
                style={{ margin: 0 }}
              >
                {count || 0}
              </AppTag>
            </StyledFlex>
          ),
          label: provider.name,
          value: provider.id,
          count: count,
        };
      })
      .sort((a, b) => {
        // First sort by count (count > 0 first)
        if (a.count > 0 && b.count === 0) return -1;
        if (a.count === 0 && b.count > 0) return 1;
        // Then sort alphabetically by label
        return a.label.localeCompare(b.label);
      });
  }, [providersData, groupedCounts]);

  const currentValue = useMemo(() => {
    if (!selectedKeys || selectedKeys.length === 0) return undefined;
    return Array.isArray(selectedKeys)
      ? selectedKeys.map((key) => String(key))
      : String(selectedKeys).split(",").filter(Boolean);
  }, [selectedKeys]);

  return (
    <DropdownSearchFilter
      field={dataIndex}
      data={filterData}
      value={currentValue}
      isLoading={isLoading}
      enableSearch={true}
      onFinish={(e) => {
        if (setSelectedKeys) {
          setSelectedKeys(e.value ? (e.value as any) : []);
        }
        if (confirm) {
          confirm();
        }
      }}
    />
  );
};

export default ProviderFilters;
